import React from 'react';
import { useIntl } from 'react-intl';
import { Grid, Hidden } from '@mui/material';
import * as css from './about.module.scss';
import SiktLogo from './siktLogo';
import commonTranslations from './welcome.i18n';
import translations from './about.i18n';
import { SmallPageTop, DesktopPageTop, BottomLinks } from './commoncomponents';
import classNames from 'classnames';
import SrOnly from '../components/screenReader/screenReader';
import { useSetDocumentTitle } from '../api/useSetDocumentTitle';
import unknownUserTranslations from './unknownUser.i18n';
import PageLayout from '../pagelayout';
import Button from '../components/button';

const AboutPage = () => {
  return (
    <>
      <div className={classNames(css.page, css.aboutPage)}>
        <Hidden only={['md', 'lg', 'xl']}>
          <AboutPageSmall>
            <InfoCards />
          </AboutPageSmall>
        </Hidden>
        <Hidden only={['xs', 'sm']}>
          <AboutPageDesktop>
            <InfoCards />
          </AboutPageDesktop>
        </Hidden>

        <footer className={css.footer}>
          <Grid container={true}>
            <Grid item={true} xs={1} />
            <Grid item={true} xs={10}>
              <SiktLogo />
            </Grid>
            <Grid item={true} xs={1} />
          </Grid>

          <BottomLinks css={css} />
        </footer>
      </div>
    </>
  );
};

const useLabels = () => {
  const { formatMessage } = useIntl();
  return {
    title: formatMessage({ id: commonTranslations.title.id }),
    aboutTitle: formatMessage({ id: commonTranslations.aboutTitle.id }),
    description: formatMessage({ id: commonTranslations.description.id }),
    studiestedInfoTitle: formatMessage({
      id: translations.studiestedInfoTitle.id,
    }),
    studiestedInfoSubtitle: formatMessage({
      id: translations.studiestedInfoSubtitle.id,
    }),
    studiestedInfoMainText: formatMessage({
      id: translations.studiestedInfoMainText.id,
    }),
    infoCardCaptionLinkText: formatMessage({
      id: translations.infoCardCaptionLinkText.id,
    }),
    studiestedInfoCaptionText: formatMessage({
      id: translations.studiestedInfoCaptionText.id,
    }),
    studentInfoTitle: formatMessage({ id: translations.studentInfoTitle.id }),
    studentInfoSubtitle: formatMessage({
      id: translations.studentInfoSubtitle.id,
    }),
    studentInfoMainText: formatMessage({
      id: translations.studentInfoMainText.id,
    }),
    studentInfoCaptionText: formatMessage({
      id: translations.studentInfoCaptionText.id,
    }),
    praksisstedInfoTitle: formatMessage({
      id: translations.praksisstedInfoTitle.id,
    }),
    praksisstedInfoSubtitle: formatMessage({
      id: translations.praksisstedInfoSubtitle.id,
    }),
    praksisstedInfoMainText: formatMessage({
      id: translations.praksisstedInfoMainText.id,
    }),
    praksisstedInfoCaptionText: formatMessage({
      id: translations.praksisstedInfoCaptionText.id,
    }),
  };
};

interface AboutPageProps {
  noDescription?: boolean;
}
const AboutPageSmall: React.FC<React.PropsWithChildren<AboutPageProps>> = ({
  noDescription,
  children,
}) => {
  const labels = useLabels();

  return (
    <div className={css.smallPage}>
      <SmallPageTop
        css={css}
        title={labels.title}
        description={noDescription ? '' : labels.description}
      />

      <main className={css.mainContent}>{children}</main>
    </div>
  );
};

const AboutPageDesktop: React.FC<React.PropsWithChildren<AboutPageProps>> = ({
  noDescription,
  children,
}) => {
  const labels = useLabels();

  return (
    <div className={css.desktop}>
      <DesktopPageTop
        css={css}
        title={labels.title}
        description={noDescription ? '' : labels.description}
        noTitleMargin
      />

      <main className={css.mainContent}>{children}</main>
    </div>
  );
};

const InfoCards = () => {
  const labels = useLabels();
  useSetDocumentTitle(labels.aboutTitle, []);

  return (
    <div className={css.infoCards}>
      <SrOnly>
        <h1>{labels.aboutTitle}</h1>
      </SrOnly>
      <InfoCard
        imageClassName={css.institutionImageStudiested}
        title={labels.studiestedInfoTitle}
        subtitle={labels.studiestedInfoSubtitle}
        mainText={labels.studiestedInfoMainText}
        captionLink="/rightsandprivacy?for=studiested"
        captionLinkText={labels.infoCardCaptionLinkText}
        captionText={labels.studiestedInfoCaptionText}
        testSelector={'infoCard-1'}
      />
      <InfoCard
        imageClassName={css.institutionImageStudent}
        title={labels.studentInfoTitle}
        subtitle={labels.studentInfoSubtitle}
        mainText={labels.studentInfoMainText}
        captionLink="/rightsandprivacy?for=student"
        captionLinkText={labels.infoCardCaptionLinkText}
        captionText={labels.studentInfoCaptionText}
        testSelector={'infoCard-2'}
      />
      <InfoCard
        imageClassName={css.institutionImagePraksissted}
        title={labels.praksisstedInfoTitle}
        subtitle={labels.praksisstedInfoSubtitle}
        mainText={labels.praksisstedInfoMainText}
        captionLink="/rightsandprivacy?for=praksissted"
        captionLinkText={labels.infoCardCaptionLinkText}
        captionText={labels.praksisstedInfoCaptionText}
        testSelector={'infoCard-3'}
      />
    </div>
  );
};

interface InfoCardProps {
  imageClassName: string;
  title: string;
  subtitle: string;
  mainText: string;
  captionLink: string;
  captionLinkText: string;
  captionText: string;
  testSelector: string;
}
const InfoCard: React.FC<React.PropsWithChildren<InfoCardProps>> = ({
  imageClassName,
  title,
  subtitle,
  mainText,
  captionLink,
  captionLinkText,
  captionText,
  testSelector,
}) => {
  return (
    <div
      className={css.infoCardWrapper}
      onClick={() => (document.location.href = captionLink)}
      data-cy={testSelector}
    >
      <div className={css.infoCard}>
        <div className={classNames(css.institutionImage, imageClassName)} />
        <h2>{title}</h2>
        <p className={css.ingress}>{subtitle}</p>
        <div dangerouslySetInnerHTML={{ __html: mainText }} />
      </div>
      <div className={css.caption}>
        <a href={captionLink}>{`${captionLinkText} ${captionText}`}</a>
      </div>
    </div>
  );
};

interface AboutInfoPageProps {
  title: string;
  mainText: string;
}
export const AboutInfoPage: React.FC<
  React.PropsWithChildren<AboutInfoPageProps>
> = ({ title, mainText }) => {
  return (
    <>
      <div className={classNames(css.page, css.infoPage)}>
        <Hidden only={['md', 'lg', 'xl']}>
          <AboutPageSmall noDescription={true}>
            <h1>{title}</h1>
            <div dangerouslySetInnerHTML={{ __html: mainText }} />
          </AboutPageSmall>
        </Hidden>
        <Hidden only={['xs', 'sm']}>
          <AboutPageDesktop noDescription={true}>
            <h1>{title}</h1>
            <div dangerouslySetInnerHTML={{ __html: mainText }} />
          </AboutPageDesktop>
        </Hidden>

        <footer>
          <div className={css.space50} />
          <Grid container={true}>
            <Grid item={true} xs={1} />
            <Grid item={true} xs={10}>
              <SiktLogo />
            </Grid>
            <Grid item={true} xs={1} />
          </Grid>

          <BottomLinks css={css} />
        </footer>
      </div>
    </>
  );
};

export const AboutCookiesPage: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { formatMessage } = useIntl();
  const labels = {
    aboutCookiesTitle: formatMessage({
      id: commonTranslations.aboutCookiesTitle.id,
    }),
    aboutCookiesText: formatMessage({
      id: commonTranslations.aboutCookiesText.id,
    }),
  };
  useSetDocumentTitle(labels.aboutCookiesTitle, []);
  return (
    <AboutInfoPage
      title={labels.aboutCookiesTitle}
      mainText={labels.aboutCookiesText}
    />
  );
};

export const AboutCookiesPageLoggedIn: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { formatMessage } = useIntl();
  const labels = {
    aboutCookiesTitle: formatMessage({
      id: commonTranslations.aboutCookiesTitle.id,
    }),
    aboutCookiesText: formatMessage({
      id: commonTranslations.aboutCookiesText.id,
    }),
  };

  return (
    <PageLayout title={labels.aboutCookiesTitle} isIndented>
      <div className={css.infoPage}>
        <div
          className={css.mainContent}
          dangerouslySetInnerHTML={{ __html: labels.aboutCookiesText }}
        />
      </div>
    </PageLayout>
  );
};

export const AboutAnalyticsPage: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { formatMessage } = useIntl();
  const labels = {
    aboutAnalyticsTitle: formatMessage({
      id: commonTranslations.aboutAnalyticsTitle.id,
    }),
    aboutAnalyticsText: formatMessage({
      id: commonTranslations.aboutAnalyticsText.id,
    }),
  };
  useSetDocumentTitle(labels.aboutAnalyticsTitle, []);
  return (
    <AboutInfoPage
      title={labels.aboutAnalyticsTitle}
      mainText={labels.aboutAnalyticsText}
    />
  );
};

export const AboutAnalyticsPageLoggedIn: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { formatMessage } = useIntl();
  const labels = {
    aboutAnalyticsTitle: formatMessage({
      id: commonTranslations.aboutAnalyticsTitle.id,
    }),
    aboutAnalyticsText: formatMessage({
      id: commonTranslations.aboutAnalyticsText.id,
    }),
  };

  return (
    <PageLayout title={labels.aboutAnalyticsTitle} isIndented>
      <div className={css.infoPage}>
        <div
          className={css.mainContent}
          dangerouslySetInnerHTML={{ __html: labels.aboutAnalyticsText }}
        />
      </div>
    </PageLayout>
  );
};

export const UnknownUserPage = () => {
  useSetDocumentTitle('Ukjent bruker - Arbeidslivsportalen', []);

  return (
    <div className={classNames(css.page, css.infoPage)}>
      <Hidden only={['md', 'lg', 'xl']}>
        <AboutPageSmall noDescription={true}>
          <UnknownUserPageContent />
        </AboutPageSmall>
      </Hidden>
      <Hidden only={['xs', 'sm']}>
        <AboutPageDesktop noDescription={true}>
          <UnknownUserPageContent />
        </AboutPageDesktop>
      </Hidden>

      <footer>
        <div className={css.space50} />
        <Grid container={true}>
          <Grid item={true} xs={1} />
          <Grid item={true} xs={10}>
            <SiktLogo />
          </Grid>
          <Grid item={true} xs={1} />
        </Grid>

        <BottomLinks css={css} />
      </footer>
    </div>
  );
};

const UnknownUserPageContent = () => {
  const { formatMessage } = useIntl();

  const labels = {
    desc: formatMessage({ id: unknownUserTranslations.description.id }),
    more: formatMessage({ id: unknownUserTranslations.more.id }),
    backbutton: formatMessage({ id: unknownUserTranslations.backbutton.id }),
  };

  const onLogoutClick = (event: React.MouseEvent) => {
    window.location.href = '/login/logout';
  };

  return (
    <>
      <h1 className={css.redTitle}>{labels.desc}</h1>
      <p>{labels.more}</p>

      <div className={css.space50} />

      <Button.Secondary onClick={onLogoutClick} data-cy="idpUnknownUser">
        {labels.backbutton}
      </Button.Secondary>
    </>
  );
};

export default AboutPage;
